import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

declare module "axios" {
  interface AxiosRequestConfig {
    // Reject 401s normally instead of showing the "session expired" toast and
    // redirecting to /signin. Used by session checks such as /api/Account/me.
    skipAuthRedirect?: boolean;
  }
}

const baseUrl =
  process.env.REACT_APP_BASE_URL || "";

// Auth is carried by the HttpOnly `access_token` cookie the API sets on login,
// so every request must include credentials. No Authorization header is sent.
const api = axios.create({
  baseURL: baseUrl,
  timeout: 120000,
  withCredentials: true,
});

// Function to show a session expired toast
const showSessionExpiredToast = (err: string) => {
  toast.error(err, {
    position: toast.POSITION.TOP_LEFT,
    autoClose: 4000, // Close the toast after 5 seconds
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const requestConfig = error.config;

    if (error.response && error.response.status === 401) {
      if (requestConfig?.skipAuthRedirect) {
        return Promise.reject(error);
      }
      showSessionExpiredToast("Session expired. Please sign in.");
      setTimeout(() => {
        window.location.href = "/signin";
      }, 4000);
      return;
    }

    if (error.code === "ECONNABORTED") {
      showSessionExpiredToast("Request timed out. Please try again later.");
      return Promise.reject(
        new Error("Request timed out. Please try again later.")
      );
    }

    return Promise.reject(error);
  }
);

export default api;
